import React, { PureComponent } from 'react'
import { Trans } from '@lingui/react'
import { I18n } from '@lingui/core'

import Checkbox from 'client/shared/blocks/checkbox/checkbox'
import Input from 'client/shared/blocks/input/input'
import { Dispatch } from 'shared/types/redux'
import {
  CancellationReason,
  loadRetentionOffer,
  loadUserCancellationReasonVariants,
  sendUserCancellationInfo,
  UserCancellationPayload,
} from 'client/bookmate/reducers/unsubscribe-reducer'
import Spacer from 'client/shared/blocks/spacer/spacer'
import Button from 'client/shared/blocks/button/button'
import ReadableDate from 'client/shared/blocks/readable-date/readable-date'
import { RecurrentSubscription } from 'client/shared/types/subscription'
import { Loader } from 'client/shared/blocks/loader/loader'

type Props = {
  dispatch: Dispatch
  cancellationReasonVariants?: CancellationReason[]
  i18n: I18n
  expiryDate: Date | null
  cancelRecurrent: () => Promise<void>
  showRetentionOffer: (arg?: string) => void
  keepSubscription: () => void
  subscription: RecurrentSubscription
  isLoading: boolean
}

type State = {
  reasons: { [key: string]: boolean }
  other: string
  comment: string
  closed: boolean
  isFormValid: boolean
}

const COMMENT_MAX_LENGTH = 160

const initialState = {
  reasons: {},
  other: '',
  comment: '',
  closed: false,
  isFormValid: false,
}

const DONT_READ_ENOUGH = 'dont_read_enough_books'
const NO_INTEREST = 'havent_found_a_book'

export class CancellationNotice extends PureComponent<Props, State> {
  state = initialState

  componentDidMount(): void {
    this.props.dispatch(loadUserCancellationReasonVariants())
  }

  sendData(): void {
    const {
      dispatch,
      cancelRecurrent,
      showRetentionOffer,
      subscription,
    } = this.props
    const { reasons, other, comment } = this.state
    const keys = Object.keys(reasons)
    const values = Object.values(reasons)
    const filtered = keys.filter((key, index) => values[index])
    const payload: UserCancellationPayload = { chosen_variants: filtered }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (reasons.other_reason) {
      payload.other_reason = other
    }
    if (comment) {
      payload.comment = comment
    }

    dispatch(sendUserCancellationInfo(payload))

    if (['mtn_ghana', 'telenor'].includes(subscription.system)) {
      cancelRecurrent()
      return
    }

    if (
      keys.includes(DONT_READ_ENOUGH) &&
      !subscription.retention_flow_special_offer_used
    ) {
      showRetentionOffer()
      return dispatch(loadRetentionOffer())
    }

    if (keys.includes(NO_INTEREST)) {
      return showRetentionOffer(NO_INTEREST)
    }

    cancelRecurrent()
  }

  markCheckbox(reason: string): void {
    this.setState({
      reasons: {
        ...this.state.reasons,
        [reason]: !this.state.reasons[reason],
      },
    })

    const cb = () => {
      const isValid = Object.values(this.state.reasons).some(
        value => value === true,
      )
      this.setState({ isFormValid: isValid })
    }

    setTimeout(cb, 200)
  }

  closeNotice(): void {
    this.props.keepSubscription()
    this.setState({ closed: true })
  }

  render(): JSX.Element | null {
    const {
      cancellationReasonVariants,
      i18n,
      expiryDate,
      isLoading,
    } = this.props

    return this.state.closed ? (
      <div className="recurrent-subscription-card__manage-button">
        <Button
          aria-label={i18n.t`plan.cancel_recurrent`}
          kind="transparent"
          onClick={() => this.setState({ closed: false })}
        >
          <Trans id="plan.cancel_recurrent" />
        </Button>
      </div>
    ) : (
      <div className="cancellation-card">
        <span className="cancellation-caption">
          <Trans id="subscription.step" values={{ maxSteps: 3, step: 1 }} />
        </span>
        <Spacer size={24} />
        <img
          src="https://a.bmstatic.com/iu/153/57/IMG-988d7ba11e61ed6ccddad82dc6c80245.svg"
          className="cancellation__decor"
          alt="bookmate"
        />
        <h4 className="cancellation__heading">
          <Trans id="subscription.cancellation_reason" />
        </h4>
        <p className="cancellation__description">
          <Trans id="subscription.cancellation_reason_description" />
        </p>
        <Spacer size={24} />
        {isLoading ? (
          <Loader color="gray" kind="big" />
        ) : (
          cancellationReasonVariants?.map((reason, index) =>
            index === cancellationReasonVariants?.length - 1 ? (
              <span className="other-reason__box">
                <Checkbox
                  checked={this.state[reason.variant]}
                  id={reason.variant}
                  label={reason.description}
                  onChange={() => this.markCheckbox(reason.variant)}
                />
                <Input
                  value={this.state.other}
                  placeholder={i18n.t`subscription.please_specify`}
                  disabled={!this.state.reasons[reason.variant]}
                  onChange={event =>
                    this.setState({ other: event?.target?.value })
                  }
                  className="other-reason__input"
                />
              </span>
            ) : (
              <>
                <Checkbox
                  checked={this.state[reason.variant]}
                  id={reason.variant}
                  label={reason.description}
                  onChange={() => this.markCheckbox(reason.variant)}
                />
                <Spacer size={24} />
              </>
            ),
          )
        )}
        <div className="comment-wrapper">
          <h5 className="comment-caption">
            <Trans id="subscription.leave_a_comment" />
          </h5>
          <span className="comment-length">
            {this.state.comment.length}/{COMMENT_MAX_LENGTH}
          </span>
        </div>
        <Spacer size={8} />
        <textarea
          maxLength={COMMENT_MAX_LENGTH}
          value={this.state.comment}
          placeholder={i18n.t`subscription.leave_a_comment_extended`}
          onChange={event => this.setState({ comment: event?.target?.value })}
          className="cancellation-comment"
        />
        <Spacer size={24} />
        <span>
          <Trans id="subscription.thanks_for_your_cooperation" />
          <br />
          {expiryDate && (
            <Trans
              id="subscription.cancellation_notice"
              components={[
                <b>
                  <ReadableDate date={expiryDate} />
                </b>,
              ]}
            />
          )}
        </span>
        <Spacer size={24} />
        <Button
          aria-label={i18n.t`subscription.keep_subscription`}
          kind="secondary"
          type="inline"
          className="button__keep"
          onClick={this.closeNotice.bind(this)}
        >
          <Trans id="subscription.keep_subscription" />
        </Button>
        <Button
          aria-label={i18n.t`subscription.cancel_subscription`}
          kind="transparent"
          type="inline"
          disabled={!this.state.isFormValid}
          className="button__cancel"
          onClick={() => this.sendData()}
        >
          <Trans id="subscription.next" />
        </Button>
      </div>
    )
  }
}
